import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { controlToastState } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "../../../api/firebase/references";
import { getFaxStatus } from "../../../api/postCall/getFaxStatus";
import { sendFax } from "../../../api/postCall/sendFax";
import { FaxOutputDocModel } from "../../../data-model/faxOutputDocModel";
import { FaxSendResponse } from "../../../data-model/faxStatusModel";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import { logError } from "../../../utils/customLogger";
import { getCorrectStatusBasedOnFaxSubmissionStatus } from "../utils/faxSubmissionHelper";

interface UseFaxSubmissionProps {
  orderId: string | undefined;
  singleMedicalPaOrder: MedicalPaOrder | null;
  orgId: string;
}

export const useFaxSubmission = ({
  orderId,
  singleMedicalPaOrder,
  orgId,
}: UseFaxSubmissionProps) => {
  const location = useLocation();
  const [faxPdfPath, setFaxPdfPath] = useState<string>("");
  const [faxIdForNotSend, setFaxIdForNotSend] = useState<string>("");

  const isFaxSubmissionFlow = useMemo(
    () => !!location.state?.faxPdfLink,
    [location.state?.faxPdfLink],
  );

  const makeSendCall = useMemo(() => {
    if (
      singleMedicalPaOrder?.status?.fax_submission_status ===
        MedicalPaOrderStatus.COMPLETED ||
      singleMedicalPaOrder?.status?.fax_submission_status ===
        MedicalPaOrderStatus.IN_PROGRESS
    ) {
      return false;
    }
    return true;
  }, [singleMedicalPaOrder?.status?.fax_submission_status]);

  const getFaxDataFromDb = useCallback(async (orderIdParam: string) => {
    const faxInfo = await FirestoreService.getDocument<FaxOutputDocModel>(
      FirestoreDocumentReference.getFaxInfoPath(orderIdParam ?? ""),
    );
    return faxInfo;
  }, []);

  useEffect(() => {
    if (!isFaxSubmissionFlow || !orderId) {
      return;
    }
    let isMounted = true;
    const getFaxData = async () => {
      try {
        const result = await getFaxDataFromDb(orderId);
        if (isMounted) {
          setFaxIdForNotSend(result?.fax_details_id ?? "");
          setFaxPdfPath(result?.filled_pdf_link ?? "");
        }
      } catch (error) {
        logError(error as Error, "Error getting fax data");
      }
    };
    getFaxData();
    return () => {
      isMounted = false;
    };
  }, [orderId, isFaxSubmissionFlow, getFaxDataFromDb]);

  const getFaxStatusAndUpdateFirestore = useCallback(
    async (faxDetailsId: string, formId: string) => {
      try {
        if (!faxDetailsId) {
          controlToastState("fax-id-not-found");
          return;
        }
        const faxStatusResponse = await getFaxStatus({
          fax_details_id: faxDetailsId,
          form_id: formId,
          org_id: orgId ?? "",
        });

        await Promise.all([
          FirestoreService.updateDocument(
            `medical_pa_orders/${orderId}/submission_info/`,
            "output",
            {
              fax_status: getCorrectStatusBasedOnFaxSubmissionStatus(
                faxStatusResponse.fax_status?.sent_status ?? "",
              ),
              fax_status_updated_at: new Date().toISOString(),
              submission: getCorrectStatusBasedOnFaxSubmissionStatus(
                faxStatusResponse.fax_status?.sent_status ?? "",
              ),
            },
          ),
          FirestoreService.updateDocument(
            FirestoreCollectionReference.medicalPaOrders(),
            singleMedicalPaOrder?.id ?? "",
            {
              "status.fax_submission_status":
                getCorrectStatusBasedOnFaxSubmissionStatus(
                  faxStatusResponse.fax_status?.sent_status ?? "",
                ),
              "status.submission": getCorrectStatusBasedOnFaxSubmissionStatus(
                faxStatusResponse.fax_status?.sent_status ?? "",
              ),
            },
          ),
        ]);

        return faxStatusResponse;
      } catch (error) {
        console.error("Error getting fax status:", error);
        controlToastState("error-getting-fax-status");
      }
    },
    [orderId, singleMedicalPaOrder?.id, orgId],
  );

  const makeSendCallAndUpdateFirestore = useCallback(
    async (faxPdfLink: string, formId: string) => {
      let sendFaxResponse: FaxSendResponse | null = null;
      try {
        if (!orgId || !faxPdfLink || !formId) {
          controlToastState("organisation-id-not-found");
          return;
        }

        // Step 1: Send fax
        if (makeSendCall) {
          sendFaxResponse = await sendFax({
            form_id: formId,
            org_id: orgId,
            file_path: faxPdfLink,
          });

          if (sendFaxResponse && sendFaxResponse?.fax_details_id) {
            await FirestoreService.updateDocument(
              `medical_pa_orders/${orderId}/submission_info/`,
              "output",
              {
                fax_details_id: sendFaxResponse?.fax_details_id,
                form_id: formId,
              },
            );
          }

          await new Promise((resolve) => setTimeout(resolve, 10000));

          // Step 2: Get fax status and update Firestore
          return await getFaxStatusAndUpdateFirestore(
            sendFaxResponse?.fax_details_id ?? "",
            formId,
          );
        } else {
          return await getFaxStatusAndUpdateFirestore(faxIdForNotSend, formId);
        }
      } catch (error) {
        console.error("Error in fax workflow:", error);
        controlToastState("error-sending-fax");
      }
    },
    [
      orgId,
      makeSendCall,
      orderId,
      getFaxStatusAndUpdateFirestore,
      faxIdForNotSend,
    ],
  );

  const submitButtonText = useMemo(() => {
    if (isFaxSubmissionFlow) {
      if (
        singleMedicalPaOrder?.status?.fax_submission_status ===
          MedicalPaOrderStatus.COMPLETED ||
        singleMedicalPaOrder?.status?.fax_submission_status ===
          MedicalPaOrderStatus.IN_PROGRESS
      ) {
        return "Continue";
      }
      return "Submit";
    }
    return "Submit";
  }, [
    isFaxSubmissionFlow,
    singleMedicalPaOrder?.status?.fax_submission_status,
  ]);

  return {
    faxPdfPath,
    faxIdForNotSend,
    isFaxSubmissionFlow,
    makeSendCall,
    makeSendCallAndUpdateFirestore,
    submitButtonText,
  };
};
